import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNavigation from '../../components/AdminNavigation';
import NavigationBreadcrumbs from '../../components/NavigationBreadcrumbs';
import { ProtectedRoute } from '../../components/AuthenticationBoundary';

import { supabase } from '../../lib/supabaseClient';

import Button from '../../components/ui/Button';
import CustomerStatsCard from './components/CustomerStatsCard';
import CustomerTableRow from './components/CustomerTableRow';
import CustomerMobileCard from './components/CustomerMobileCard';
import CustomerDetailsModal from './components/CustomerDetailsModal';
import CustomerOrdersModal from './components/CustomerOrdersModal';
import SearchFilterBar from './components/SearchFilterBar';
import EmptyState from './components/EmptyState';

const AdminCustomerManagement = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [orderFilter, setOrderFilter] = useState('all');
  const [spendingFilter, setSpendingFilter] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showOrdersModal, setShowOrdersModal] = useState(false);

  const PAGE_SIZE = 20;
  const [page, setPage] = useState(1);
  const [customers, setCustomers] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState('');

  const formatDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  };

  const fetchCustomers = async () => {
    setIsFetching(true);
    setFetchError('');

    try {
      const { data: orders, error: ordersError } = await supabase
        .from('orders')
        .select('id, customer_id, customer_name, phone, address, order_date, total_amount')
        .order('order_date', { ascending: false });

      if (ordersError) throw ordersError;

      const aggByCustomerId = new Map();
      (orders || []).forEach((o) => {
        const rawCustomerId = o?.customer_id;
        const phone = o?.phone;
        const name = o?.customer_name;

        const key = rawCustomerId || (phone ? `phone:${phone}` : (name ? `name:${name}` : null));
        if (!key) return;

        const prev = aggByCustomerId.get(key) || {
          id: key,
          customerId: rawCustomerId || null,
          name: null,
          phone: null,
          address: null,
          firstOrderDate: null,
          lastOrderDate: null,
          totalOrders: 0,
          totalSpent: 0,
        };

        const orderDate = o?.order_date ? new Date(o.order_date) : null;
        const prevLast = prev?.lastOrderDate ? new Date(prev.lastOrderDate) : null;
        const prevFirst = prev?.firstOrderDate ? new Date(prev.firstOrderDate) : null;

        const next = {
          ...prev,
          customerId: prev.customerId || rawCustomerId || null,
          name: prev.name || name || null,
          phone: prev.phone || phone || null,
          address: prev.address || o?.address || null,
          totalOrders: prev.totalOrders + 1,
          totalSpent: prev.totalSpent + Number(o?.total_amount || 0),
          lastOrderDate:
            orderDate && (!prevLast || orderDate > prevLast) ? o.order_date : prev.lastOrderDate,
          firstOrderDate:
            orderDate && (!prevFirst || orderDate < prevFirst) ? o.order_date : prev.firstOrderDate,
        };

        aggByCustomerId.set(key, next);
      });

      const rows = Array.from(aggByCustomerId.values()).map((agg) => {
        const totalSpent = Number(agg?.totalSpent || 0);
        const totalOrders = Number(agg?.totalOrders || 0);

        let status = 'active';
        if (totalSpent >= 50000) status = 'vip';

        return {
          id: agg?.id,
          customerId: agg?.customerId,
          name: agg?.name,
          phone: agg?.phone,
          address: agg?.address,
          registrationDate: formatDate(agg?.firstOrderDate),
          totalOrders,
          lastOrderDate: formatDate(agg?.lastOrderDate),
          totalSpent,
          status,
        };
      });

      rows.sort((a, b) => {
        const aT = a?.lastOrderDate ? new Date(a.lastOrderDate).getTime() : 0;
        const bT = b?.lastOrderDate ? new Date(b.lastOrderDate).getTime() : 0;
        return bT - aT;
      });

      setCustomers(rows);
    } catch (e) {
      setFetchError(e?.message || '고객 데이터를 불러오지 못했습니다.');
      setCustomers([]);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, orderFilter, spendingFilter]);

  const filteredCustomers = useMemo(() => {
    let filtered = customers;

    if (searchQuery) {
      filtered = filtered?.filter(customer =>
        customer?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        customer?.phone?.includes(searchQuery)
      );
    }

    if (orderFilter !== 'all') {
      filtered = filtered?.filter(customer => {
        switch (orderFilter) {
          case 'high':
            return customer?.totalOrders >= 10;
          case 'medium':
            return customer?.totalOrders >= 5 && customer?.totalOrders < 10;
          case 'low':
            return customer?.totalOrders >= 1 && customer?.totalOrders < 5;
          case 'none':
            return customer?.totalOrders === 0;
          default:
            return true;
        }
      });
    }

    if (spendingFilter !== 'all') {
      filtered = filtered?.filter(customer => {
        switch (spendingFilter) {
          case 'vip':
            return customer?.totalSpent >= 50000;
          case 'high':
            return customer?.totalSpent >= 30000 && customer?.totalSpent < 50000;
          case 'medium':
            return customer?.totalSpent >= 10000 && customer?.totalSpent < 30000;
          case 'low':
            return customer?.totalSpent < 10000;
          default:
            return true;
        }
      });
    }

    return filtered;
  }, [customers, searchQuery, orderFilter, spendingFilter]);

  const stats = useMemo(() => {
    const totalCustomers = customers?.length;
    const activeCustomers = customers?.filter(c => c?.status === 'active' || c?.status === 'vip')?.length;
    const vipCustomers = customers?.filter(c => c?.status === 'vip')?.length;
    const totalRevenue = customers?.reduce((sum, c) => sum + c?.totalSpent, 0);

    return {
      totalCustomers,
      activeCustomers,
      vipCustomers,
      totalRevenue
    };
  }, [customers]);

  const handleViewDetails = (customer) => {
    setSelectedCustomer(customer);
    setShowDetailsModal(true);
  };

  const handleViewOrders = (customer) => {
    setSelectedCustomer(customer);
    setShowOrdersModal(true);
  };

  const handleReset = () => {
    setSearchQuery('');
    setOrderFilter('all');
    setSpendingFilter('all');
  };

  const totalPages = Math.max(1, Math.ceil((filteredCustomers?.length || 0) / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * PAGE_SIZE;
  const pageCustomers = filteredCustomers?.slice(startIndex, startIndex + PAGE_SIZE) || [];

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-background">
        <AdminNavigation />
        <NavigationBreadcrumbs />

        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">고객 관리</h1>
              <p className="mt-1 text-sm text-muted-foreground text-white">
                고객 정보 및 이용 통계를 관리합니다
              </p>
            </div>
          </div>

          <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <CustomerStatsCard
              icon="Users"
              label="전체 고객"
              value={stats?.totalCustomers}
            />
            <CustomerStatsCard
              icon="UserCheck"
              label="활성 고객"
              value={stats?.activeCustomers}
            />
            <CustomerStatsCard
              icon="Crown"
              label="VIP 고객"
              value={stats?.vipCustomers}
            />
            <CustomerStatsCard
              icon="DollarSign"
              label="총 매출"
              value={`${stats?.totalRevenue?.toLocaleString('ko-KR')}원`}
            />
          </div>

          <div className="mb-6">
            <SearchFilterBar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              orderFilter={orderFilter}
              onOrderFilterChange={setOrderFilter}
              spendingFilter={spendingFilter}
              onSpendingFilterChange={setSpendingFilter}
              onReset={handleReset}
            />
          </div>

          {fetchError && (
            <div className="mb-6 rounded-lg bg-error/10 p-4">
              <p className="text-sm text-error">{fetchError}</p>
            </div>
          )}

          {isFetching ? (
            <div className="rounded-lg bg-card p-8 text-center text-sm text-muted-foreground shadow-card">
              고객 데이터를 불러오는 중...
            </div>
          ) : filteredCustomers?.length === 0 ? (
            <EmptyState searchQuery={searchQuery} />
          ) : (
            <>
              <div className="hidden lg:block">
                <div className="overflow-hidden rounded-lg bg-card shadow-card">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                            고객 정보
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                            가입일
                          </th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-foreground">
                            총 주문
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                            최근 주문
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                            총 결제액
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                            상태
                          </th>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">
                            작업
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {pageCustomers?.map((customer) => (
                          <CustomerTableRow
                            key={customer?.id}
                            customer={customer}
                            onViewDetails={handleViewDetails}
                            onViewOrders={handleViewOrders}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="space-y-4 lg:hidden">
                {pageCustomers?.map((customer) => (
                  <CustomerMobileCard
                    key={customer?.id}
                    customer={customer}
                    onViewDetails={handleViewDetails}
                    onViewOrders={handleViewOrders}
                  />
                ))}
              </div>

              <div className="mt-6 flex flex-col gap-3 rounded-lg bg-card p-4 shadow-card sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm text-muted-foreground">
                  총 <span className="font-medium text-foreground">{filteredCustomers?.length || 0}</span>명
                </div>
                <div className="flex items-center justify-between gap-3 sm:justify-end">
                  <span className="text-sm text-muted-foreground">
                    페이지 <span className="font-medium text-foreground">{safePage}</span> / {totalPages}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={safePage <= 1}
                    >
                      이전
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={safePage >= totalPages}
                    >
                      다음
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </main>

        {showDetailsModal && (
          <CustomerDetailsModal
            customer={selectedCustomer}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedCustomer(null);
            }}
          />
        )}

        {showOrdersModal && (
          <CustomerOrdersModal
            customer={selectedCustomer}
            onClose={() => {
              setShowOrdersModal(false);
              setSelectedCustomer(null);
            }}
          />
        )}
      </div>
    </ProtectedRoute>
  );
};

export default AdminCustomerManagement;